import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Bounce, toast } from 'react-toastify';
import { Avatar } from './AvatarGroup';
import ProgressBar from './ProgressBar';
import StartSessionDropdown from './StartSessionDropdown';
import TaskAddCollaboratorDropdown from './TaskAddCollaboratorDropdown';
import TaskStatusDropdown, { STATUS_OPTIONS } from './TaskStatusDropdown';
import DeleteConfirmation from '../Common/DeleteConfirmation';
import CustomIconButton from '../OnboardingComponents/CustomIconButton';
import { ICONS } from '../../constants/icons';
import { del, get, patch } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { toastApiError, getApiErrorMessage } from '../../helpers/apiError';
import { getUserAvatarUrl } from '../../helpers/userAvatar';
import { getUserFullName } from '../../helpers/userDisplay';
import { formatWorkedHours } from '../../helpers/sessionDetail';
import { celebrateImpression } from './ImpressionCelebration';
import {
  SESSION_COLLAB_REACTION,
  addSessionCollaboratorReaction,
  countsFromReactionRows,
  countsFromReactionSummary,
  emptyImpression,
  getSessionCollaboratorReactions,
  impressionFromReaction,
  removeSessionCollaboratorReaction,
  resolveTaskSessionAction,
  taskSessionActionLabel,
} from '../../helpers/session';

const STATUS_LABEL_BY_API = {
  in_progress: 'In Progress',
  completed: 'Completed',
  pending: 'Not Started',
  not_started: 'Not Started',
};

const STATUS_API_BY_LABEL = {
  'In Progress': 'in_progress',
  Completed: 'completed',
  'Not Started': 'pending',
};

const mapTaskStatusLabel = (value) => {
  if (!value) return null;
  if (STATUS_OPTIONS.some((option) => option.label === value)) return value;
  const key = String(value).toLowerCase().replace(/[\s-]+/g, '_');
  return STATUS_LABEL_BY_API[key] || null;
};

const resolveTaskCreatorId = (taskLike) => {
  if (!taskLike) return '';
  const creator = taskLike.creator || taskLike.user;
  if (creator?.id != null) return String(creator.id);
  if (taskLike.creator_id != null) return String(taskLike.creator_id);
  if (taskLike.user_id != null) return String(taskLike.user_id);
  if (taskLike.created_by != null) return String(taskLike.created_by);
  return '';
};

const toIsoDate = (value) => {
  if (!value || value === '—' || value === 'Ongoing') return '';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const formatDisplayDate = (iso) => {
  const value = toIsoDate(iso);
  if (!value) return '—';
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const formatActivityGroupDate = (iso) => formatDisplayDate(iso);

const formatClock = (value) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return [date.getHours(), date.getMinutes(), date.getSeconds()]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
};

const formatLoggedHrs = (value, isLeft) => {
  if (!isLeft) return '--';
  return formatWorkedHours(value);
};

const activityUserName = (user) =>
  getUserFullName(user, { fallback: 'Collaborator' });

const mapSessionActivityCard = (row) => {
  const status = String(row?.status || '').toLowerCase();
  const state = String(row?.current_state || '').toLowerCase();
  const isLeft =
    status === 'left' || status === 'exited' || state === 'ended';
  const joinedAtRaw = row?.joined_at || row?.created_at || '';

  return {
    id: row?.id,
    sessionId: row?.session_id || row?.session?.id || null,
    userId: row?.user?.id || row?.user_id || null,
    user: {
      name: activityUserName(row?.user),
      avatar: getUserAvatarUrl(row?.user),
    },
    status: isLeft ? 'Exited' : 'In Session',
    isLeft,
    loggedHours: formatLoggedHrs(row?.total_worked_hours, isLeft),
    joinNote: row?.working_note || '',
    joinTime: formatClock(joinedAtRaw),
    exitNote: isLeft ? row?.leave_note || '' : '(Still in Session)',
    exitTime: isLeft ? formatClock(row?.left_at || row?.updated_at) : '--',
    joinedAtRaw,
    activityDateKey: toIsoDate(joinedAtRaw) || 'unknown',
    activityDateLabel: formatActivityGroupDate(joinedAtRaw),
  };
};

const unwrapTaskPayload = (payload) => {
  if (!payload) return null;
  if (payload.id) return payload;
  if (payload.data?.id) return payload.data;
  if (Array.isArray(payload.data)) return payload.data[0] || null;
  return null;
};

const mapFetchedCollaborators = (taskPayload) => {
  const list = Array.isArray(taskPayload?.collaborators)
    ? taskPayload.collaborators
    : [];
  return list
    .map((person) => {
      const user = person?.user || {};
      const name =
        getUserFullName(user) ||
        getUserFullName(person) ||
        '';
      if (!name) return null;
      return {
        id: person.id || user.id,
        userId: person.user_id || user.id || '',
        email: user.email || person.email || '',
        name,
        src: getUserAvatarUrl(user),
        avatar: getUserAvatarUrl(user),
      };
    })
    .filter(Boolean);
};

const mapFetchedTaskDetail = (payload, fallback = {}) => {
  if (!payload?.id) return null;

  const occurrenceRaw =
    payload.occurrence_date ||
    payload.end_date ||
    payload.start_date ||
    fallback.occurrenceDate ||
    fallback.dueDate ||
    '';
  const occurrenceDate = toIsoDate(occurrenceRaw);
  const estimated = Number(
    payload.estimated_hours ?? fallback.timeTracking?.total ?? 0
  );
  const logged = Number(
    payload.total_hours_logged ??
      payload.logged_hours ??
      fallback.timeTracking?.logged ??
      0
  );
  const fetchedCollaborators = mapFetchedCollaborators(payload);
  const totalCollaborators = Number(payload.total_collaborators);
  const creator = payload.creator || payload.user || fallback.creator || null;
  const creatorId =
    resolveTaskCreatorId(payload) ||
    resolveTaskCreatorId(fallback) ||
    '';

  return {
    id: payload.id,
    title: payload.title || payload.name || fallback.title || 'Untitled Task',
    description:
      payload.description != null
        ? payload.description
        : fallback.description || '',
    dueDate: occurrenceDate
      ? formatDisplayDate(occurrenceDate)
      : fallback.dueDate || '—',
    occurrenceDate,
    collaborators:
      fetchedCollaborators.length > 0
        ? fetchedCollaborators
        : fallback.collaborators || [],
    totalCollaborators: Number.isFinite(totalCollaborators)
      ? totalCollaborators
      : fetchedCollaborators.length || fallback.totalCollaborators || 0,
    timeTracking: {
      total: Number.isFinite(estimated) ? estimated : 0,
      logged: Number.isFinite(logged) ? logged : 0,
      remaining: Math.max(
        0,
        Number(
          (
            (Number.isFinite(estimated) ? estimated : 0) -
            (Number.isFinite(logged) ? logged : 0)
          ).toFixed(2)
        )
      ),
    },
    sessionActivity: fallback.sessionActivity || [],
    status: mapTaskStatusLabel(payload.status) || fallback.status,
    creator,
    creatorId,
  };
};

/**
 * ProjectTaskDetail
 * ------------------------------------------------------------------
 * Full-viewport portal panel with Asana-style inline edit for title,
 * due date, and description; PATCH + DELETE via project task APIs.
 */
const ProjectTaskDetail = ({
  task: taskProp,
  owner,
  onClose,
  onStartSession,
  startSessionDisabled = false,
  onStatusChange,
  onCollaboratorsAdded,
  onTaskUpdated,
  onTaskDeleted,
}) => {
  const { project } = useOutletContext() || {};
  const currentUser = useSelector((state) => state.auth.currentUser);
  const currentUserId = currentUser?.id || '';
  const [fetchedTask, setFetchedTask] = useState(null);
  const [isTaskLoading, setIsTaskLoading] = useState(false);
  const [sessionRows, setSessionRows] = useState([]);
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  const [impressions, setImpressions] = useState({});
  const [reactingMemberId, setReactingMemberId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftOccurrenceDate, setDraftOccurrenceDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const titleInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const savingRef = useRef(false);

  const task = useMemo(() => {
    if (!taskProp) return null;
    if (!fetchedTask || String(fetchedTask.id) !== String(taskProp.id)) {
      return taskProp;
    }
    return {
      ...taskProp,
      ...fetchedTask,
      // Detail edits (e.g. status change) win over stale parent list payloads.
      status: fetchedTask.status ?? taskProp.status,
      completed: fetchedTask.completed ?? taskProp.completed,
      sessionAction: taskProp.sessionAction ?? fetchedTask.sessionAction,
      action: taskProp.action ?? fetchedTask.action,
    };
  }, [taskProp, fetchedTask]);

  const projectId = project?.id || '';

  const handleStatusChange = useCallback(
    async (nextLabel) => {
      if (!nextLabel || !task?.id) return;

      const currentStatus =
        mapTaskStatusLabel(task?.status) ||
        (task?.completed ? 'Completed' : 'In Progress');
      if (nextLabel === currentStatus) return;

      const previousStatus = currentStatus;
      const nextCompleted = nextLabel === 'Completed';
      const nextPatch = { status: nextLabel, completed: nextCompleted };

      setFetchedTask((prev) =>
        prev && String(prev.id) === String(task.id)
          ? { ...prev, ...nextPatch }
          : prev
      );

      if (onStatusChange) {
        onStatusChange(nextLabel);
        return;
      }

      if (!projectId) return;

      const apiStatus = STATUS_API_BY_LABEL[nextLabel];
      if (!apiStatus) return;

      try {
        const response = await patch(
          endpoints.updateTaskStatus(projectId, task.id),
          { status: apiStatus }
        );

        if (!response?.data?.success) {
          throw new Error(
            response?.data?.message || 'Failed to update status'
          );
        }

        onTaskUpdated?.(task.id, nextPatch);
        toast.success(response?.data?.message || 'Status updated.', {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        });
      } catch (err) {
        setFetchedTask((prev) =>
          prev && String(prev.id) === String(task.id)
            ? {
                ...prev,
                status: previousStatus,
                completed: previousStatus === 'Completed',
              }
            : prev
        );
        toastApiError(err, 'Failed to update status');
      }
    },
    [onStatusChange, onTaskUpdated, projectId, task]
  );

  const collaborators = useMemo(
    () => task?.collaborators || [],
    [task?.collaborators]
  );
  const isCurrentUserTaskEditor = useMemo(() => {
    if (!currentUserId || !task?.id) return false;
    if (owner?.isYou) return true;
    if (task.creatorId && String(task.creatorId) === String(currentUserId)) return true;
    if (task.creator?.id && String(task.creator.id) === String(currentUserId)) return true;
    return (task.collaborators || []).some(
      (c) => String(c.userId || c.user?.id || c.id) === String(currentUserId)
    );
  }, [currentUserId, task, owner?.isYou]);
  const canEdit = useMemo(
    () =>
      Boolean(
        projectId &&
          task?.id &&
          project?.is_current_user_active_member &&
          isCurrentUserTaskEditor
      ),
    [projectId, task?.id, project?.is_current_user_active_member, isCurrentUserTaskEditor]
  );
  const canDelete = useMemo(() => {
    if (!projectId || !task?.id || !currentUserId) return false;
    const creatorId = resolveTaskCreatorId(task);
    if (creatorId) return creatorId === String(currentUserId);
    return Boolean(owner?.isYou);
  }, [projectId, task, currentUserId, owner?.isYou]);

  const existingMemberKeys = useMemo(() => {
    const keys = [];
    collaborators.forEach((person) => {
      [
        person.id,
        person.userId,
        person.user?.id,
        person.email,
        person.user?.email,
      ].forEach((value) => {
        if (value) keys.push(String(value).trim().toLowerCase());
      });
    });
    if (owner?.id) keys.push(String(owner.id).trim().toLowerCase());
    return [...new Set(keys)];
  }, [collaborators, owner?.id]);

  const syncDraftsFromTask = useCallback((nextTask) => {
    setDraftTitle(nextTask?.title || '');
    setDraftDescription(nextTask?.description || '');
    setDraftOccurrenceDate(
      toIsoDate(nextTask?.occurrenceDate || nextTask?.dueDate || '')
    );
  }, []);

  useEffect(() => {
    const taskId = taskProp?.id;
    if (!taskId) {
      setFetchedTask(null);
      setIsTaskLoading(false);
      return undefined;
    }

    let cancelled = false;
    setFetchedTask(null);
    setIsTaskLoading(true);

    const loadTask = async () => {
      try {
        const response = await get(endpoints.getTaskById(taskId), {
          params: { 'with[]': ['collaborators.user', 'creator'] },
          skipErrorToast: true,
        });
        if (cancelled) return;

        if (!response?.data?.success) {
          setFetchedTask(null);
          return;
        }

        const mapped = mapFetchedTaskDetail(
          unwrapTaskPayload(response.data.result),
          taskProp
        );
        setFetchedTask(mapped);
      } catch {
        if (!cancelled) setFetchedTask(null);
      } finally {
        if (!cancelled) setIsTaskLoading(false);
      }
    };

    loadTask();
    return () => {
      cancelled = true;
    };
  }, [taskProp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!task) return;
    syncDraftsFromTask(task);
    setEditingField(null);
  }, [task?.id, fetchedTask?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!task || editingField) return;
    syncDraftsFromTask(task);
  }, [
    task?.title,
    task?.description,
    task?.occurrenceDate,
    task?.dueDate,
    editingField,
    syncDraftsFromTask,
    task,
  ]);

  useEffect(() => {
    const taskId = task?.id;
    if (!taskId) {
      setSessionRows([]);
      setImpressions({});
      setIsActivityLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadActivity = async () => {
      setIsActivityLoading(true);
      const response = await get(endpoints.getSessionsCollaborators, {
        params: {
          'with[]': ['user'],
          'task_ids[]': [taskId],
        },
        skipErrorToast: true,
      });

      if (cancelled) return;

      if (response?.data?.success) {
        setSessionRows(response.data.result?.data || []);
      } else {
        setSessionRows([]);
      }
      setIsActivityLoading(false);
    };

    loadActivity();
    return () => {
      cancelled = true;
    };
  }, [task?.id]);

  useEffect(() => {
    if (editingField === 'title') titleInputRef.current?.focus();
    if (editingField === 'dueDate') dateInputRef.current?.focus();
    if (editingField === 'description') descriptionInputRef.current?.focus();
  }, [editingField]);

  const activityCards = useMemo(
    () => sessionRows.map(mapSessionActivityCard),
    [sessionRows]
  );

  const activityGroups = useMemo(() => {
    const byDate = new Map();

    activityCards.forEach((card) => {
      const dateKey = card.activityDateKey || 'unknown';
      if (!byDate.has(dateKey)) {
        byDate.set(dateKey, {
          dateKey,
          dateLabel: card.activityDateLabel || 'Unknown date',
          cards: [],
        });
      }
      byDate.get(dateKey).cards.push(card);
    });

    return Array.from(byDate.values())
      .sort((a, b) => b.dateKey.localeCompare(a.dateKey))
      .map((group) => ({
        ...group,
        cards: [...group.cards].sort(
          (a, b) =>
            new Date(b.joinedAtRaw || 0).getTime() -
            new Date(a.joinedAtRaw || 0).getTime()
        ),
      }));
  }, [activityCards]);

  const loadActivityImpressions = useCallback(
    async (cards) => {
      if (!Array.isArray(cards) || cards.length === 0) {
        setImpressions({});
        return;
      }

      const results = await Promise.all(
        cards.map(async (card) => {
          const collaboratorId = card?.id;
          const sessionId = card?.sessionId;
          if (!collaboratorId || !sessionId) return null;

          const result = await getSessionCollaboratorReactions(
            sessionId,
            collaboratorId
          );
          if (!result.ok) {
            return {
              id: String(collaboratorId),
              impression: emptyImpression(),
            };
          }

          const mine =
            result.rows.find((row) => row?.is_current_user) ||
            result.rows.find(
              (row) =>
                currentUserId &&
                String(row?.user?.id || row?.user_id || '') ===
                  String(currentUserId)
            ) ||
            null;
          const summary =
            result.result?.reaction_summary ||
            result.result?.reactionSummary ||
            null;
          const counts = Array.isArray(summary)
            ? countsFromReactionSummary(summary)
            : countsFromReactionRows(result.rows);
          const personal = impressionFromReaction(mine?.reaction);

          return {
            id: String(collaboratorId),
            impression: {
              ...personal,
              likeCount: counts.likeCount,
              heartCount: counts.heartCount,
            },
          };
        })
      );

      const next = {};
      results.forEach((item) => {
        if (!item?.id) return;
        next[item.id] = item.impression;
      });
      setImpressions(next);
    },
    [currentUserId]
  );

  useEffect(() => {
    if (isActivityLoading) return undefined;
    loadActivityImpressions(activityCards);
    return undefined;
  }, [activityCards, isActivityLoading, loadActivityImpressions]);

  const handleToggleImpression = useCallback(
    async (sessionId, memberId, type) => {
      if (!sessionId || !memberId || reactingMemberId) return false;
      if (type !== 'liked' && type !== 'hearted') return false;

      const key = String(memberId);
      const current = impressions[key] || emptyImpression();
      const targetEmoji =
        type === 'liked'
          ? SESSION_COLLAB_REACTION.like
          : SESSION_COLLAB_REACTION.heart;
      const isRemoving = Boolean(current[type]);
      const likeCount = Number(current.likeCount || 0);
      const heartCount = Number(current.heartCount || 0);

      let optimistic;
      if (isRemoving) {
        optimistic = {
          liked: false,
          hearted: false,
          reaction: null,
          likeCount:
            type === 'liked' ? Math.max(0, likeCount - 1) : likeCount,
          heartCount:
            type === 'hearted' ? Math.max(0, heartCount - 1) : heartCount,
        };
      } else {
        const personal = impressionFromReaction(targetEmoji);
        optimistic = {
          ...personal,
          likeCount:
            type === 'liked'
              ? likeCount + (current.liked ? 0 : 1)
              : Math.max(0, likeCount - (current.liked ? 1 : 0)),
          heartCount:
            type === 'hearted'
              ? heartCount + (current.hearted ? 0 : 1)
              : Math.max(0, heartCount - (current.hearted ? 1 : 0)),
        };
      }

      setReactingMemberId(key);
      setImpressions((prev) => ({ ...prev, [key]: optimistic }));

      try {
        let result;
        if (isRemoving) {
          result = await removeSessionCollaboratorReaction(
            sessionId,
            memberId,
            current.reaction || targetEmoji
          );
        } else {
          result = await addSessionCollaboratorReaction(
            sessionId,
            memberId,
            targetEmoji
          );
        }

        if (!result.ok) {
          setImpressions((prev) => ({ ...prev, [key]: current }));
          toastApiError(result.response, 'Failed to update impression');
          return false;
        }

        if (!isRemoving) {
          celebrateImpression(type);
        }

        const summary =
          result.result?.reaction_summary ||
          result.result?.reactionSummary ||
          null;
        if (Array.isArray(summary)) {
          const counts = countsFromReactionSummary(summary);
          setImpressions((prev) => ({
            ...prev,
            [key]: {
              ...(prev[key] || optimistic),
              likeCount: counts.likeCount,
              heartCount: counts.heartCount,
            },
          }));
        }
        return true;
      } catch (err) {
        setImpressions((prev) => ({ ...prev, [key]: current }));
        toastApiError(err, 'Failed to update impression');
        return false;
      } finally {
        setReactingMemberId(null);
      }
    },
    [impressions, reactingMemberId]
  );

  const saveTaskFields = useCallback(
    async (fields) => {
      if (!canEdit || savingRef.current) return false;

      const payload = {
        title: fields.title ?? draftTitle,
        description: fields.description ?? draftDescription,
        occurrence_date: fields.occurrenceDate ?? draftOccurrenceDate,
      };

      const currentIso = toIsoDate(task?.occurrenceDate || '');
      const unchanged =
        String(payload.title || '').trim() === String(task?.title || '').trim() &&
        String(payload.description || '') === String(task?.description || '') &&
        String(payload.occurrence_date || '') === String(currentIso || '');

      if (unchanged) return true;

      savingRef.current = true;
      setIsSaving(true);
      try {
        const response = await patch(
          endpoints.updateTask(projectId, task.id),
          payload
        );

        if (response?.data?.success === false) {
          throw new Error(
            response?.data?.message || 'Failed to update task'
          );
        }

        const nextOccurrence = toIsoDate(payload.occurrence_date);
        const nextPatch = {
          title: payload.title,
          description: payload.description,
          occurrenceDate: nextOccurrence,
          dueDate: formatDisplayDate(nextOccurrence),
        };
        setFetchedTask((prev) =>
          prev && String(prev.id) === String(task.id)
            ? { ...prev, ...nextPatch }
            : prev
        );
        onTaskUpdated?.(task.id, nextPatch);
        toast.success(response?.data?.message || 'Task updated.', {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        });
        return true;
      } catch (err) {
        toastApiError(err, 'Failed to update task');
        syncDraftsFromTask(task);
        return false;
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [
      canEdit,
      draftDescription,
      draftOccurrenceDate,
      draftTitle,
      onTaskUpdated,
      projectId,
      syncDraftsFromTask,
      task,
    ]
  );

  const beginEdit = (field) => {
    if (!canEdit || isSaving) return;
    syncDraftsFromTask(task);
    setEditingField(field);
  };

  const cancelEdit = () => {
    syncDraftsFromTask(task);
    setEditingField(null);
  };

  const finishEdit = async (field) => {
    if (editingField !== field) return;
    setEditingField(null);
    await saveTaskFields({
      title: draftTitle,
      description: draftDescription,
      occurrenceDate: draftOccurrenceDate,
    });
  };

  const clearDueDate = async (event) => {
    event.stopPropagation();
    if (!canEdit || isSaving) return;
    setDraftOccurrenceDate('');
    setEditingField(null);
    await saveTaskFields({ occurrenceDate: '' });
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete || isDeleting) return;
    setIsDeleting(true);
    try {
      const response = await del(endpoints.deleteTask(projectId, task.id), {
        skipErrorToast: true,
      });

      if (!response?.data?.success) {
        toast.error(
          response?.data?.message ||
            getApiErrorMessage(response, 'Failed to delete task'),
          {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          }
        );
        return;
      }

      toast.success(response?.data?.message || 'Task deleted.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      setDeleteOpen(false);
      onTaskDeleted?.(task.id);
      onClose?.();
    } catch (err) {
      toastApiError(err, 'Failed to delete task');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!task) return null;

  const loggedSafe = Number(
    task.total_hours_logged ?? task.timeTracking?.logged ?? task.logged
  );
  const estimatedSafe = Number(
    task.estimated_hours ?? task.timeTracking?.total ?? task.estimated
  );
  const loggedHours = Number.isFinite(loggedSafe) ? loggedSafe : 0;
  const estimatedHours = Number.isFinite(estimatedSafe) ? estimatedSafe : 0;
  const remainingSafe =
    task.timeTracking?.remaining != null &&
    Number.isFinite(Number(task.timeTracking.remaining))
      ? Number(task.timeTracking.remaining)
      : Math.max(0, Number((estimatedHours - loggedHours).toFixed(2)));

  const statusLabel =
    mapTaskStatusLabel(task.status) ||
    (task.completed ? 'Completed' : 'In Progress');
  const visibleCollaborators = collaborators.slice(0, 3);
  const collaboratorTotalRaw =
    task.total_collaborators ?? task.totalCollaborators;
  const collaboratorTotalNum = Number(collaboratorTotalRaw);
  const collaboratorTotal = Number.isFinite(collaboratorTotalNum)
    ? collaboratorTotalNum
    : collaborators.length;
  const loggedHrs = formatWorkedHours(loggedHours);
  const totalHrs = formatWorkedHours(estimatedHours);
  const remainingHrs = formatWorkedHours(remainingSafe);
  const displayDueDate = formatDisplayDate(
    draftOccurrenceDate || task.occurrenceDate
  );

  const sessionAction = resolveTaskSessionAction(task);
  const sessionLabel = taskSessionActionLabel(sessionAction);

  return createPortal(
    <>
      <div className="project-task-detail__backdrop" onClick={onClose} />
      <aside
        className="project-task-detail"
        role="dialog"
        aria-modal="true"
        aria-label="Task detail"
        aria-busy={isTaskLoading || undefined}
      >
        <header className="project-task-detail__header">
          <div className="project-task-detail__title-row">
            <div className="project-task-detail__heading">
              <TaskStatusDropdown
                value={statusLabel}
                onChange={canEdit ? handleStatusChange : undefined}
              />
              {editingField === 'title' ? (
                <input
                  ref={titleInputRef}
                  type="text"
                  className="project-task-detail__title-input"
                  value={draftTitle}
                  disabled={isSaving}
                  aria-label="Task title"
                  onChange={(event) => setDraftTitle(event.target.value)}
                  onBlur={() => finishEdit('title')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.currentTarget.blur();
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      cancelEdit();
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="project-task-detail__title"
                  onClick={() => beginEdit('title')}
                  disabled={!canEdit}
                >
                  {draftTitle || task.title}
                </button>
              )}
            </div>
            <div className="project-task-detail__header-actions">
              {canDelete ? (
                <button
                  type="button"
                  className="project-task-detail__icon-btn"
                  aria-label="Delete task"
                  onClick={() => setDeleteOpen(true)}
                  disabled={isDeleting}
                >
                  <img src={ICONS.trash} alt="" width={20} height={20} />
                </button>
              ) : null}
              <button
                type="button"
                className="project-task-detail__icon-btn"
                onClick={onClose}
                aria-label="Close"
              >
                <img src={ICONS.logout} alt="" width={20} height={20} />
              </button>
            </div>
          </div>

          <div className="project-task-detail__meta-row">
            <div className="project-task-detail__due">
              <span>Due Date:</span>
              {editingField === 'dueDate' ? (
                <span className="project-task-detail__due-edit">
                  <input
                    ref={dateInputRef}
                    type="date"
                    className="project-task-detail__due-input"
                    value={draftOccurrenceDate}
                    disabled={isSaving}
                    aria-label="Due date"
                    onChange={(event) =>
                      setDraftOccurrenceDate(event.target.value)
                    }
                    onBlur={() => finishEdit('dueDate')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        event.currentTarget.blur();
                      }
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        cancelEdit();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="project-task-detail__due-clear-btn"
                    aria-label="Clear due date"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={clearDueDate}
                  >
                    <img src={ICONS.callRemoveX} alt="" width={12} height={12} />
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="project-task-detail__due-value"
                  onClick={() => beginEdit('dueDate')}
                  disabled={!canEdit}
                >
                  <b>{displayDueDate}</b>
                  {draftOccurrenceDate || task.occurrenceDate ? (
                    <span
                      role="button"
                      tabIndex={0}
                      className="project-task-detail__due-clear-btn"
                      aria-label="Clear due date"
                      onClick={clearDueDate}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          clearDueDate(event);
                        }
                      }}
                    >
                      <img
                        src={ICONS.callRemoveX}
                        alt=""
                        width={12}
                        height={12}
                      />
                    </span>
                  ) : null}
                </button>
              )}
            </div>
            <div className="project-task-detail__collabs-row">
              {projectId && task.id && canEdit ? (
                <TaskAddCollaboratorDropdown
                  projectId={projectId}
                  taskId={task.id}
                  existingMemberKeys={existingMemberKeys}
                  onAdded={onCollaboratorsAdded}
                />
              ) : null}
              <div className="project-task-detail__collabs">
                {visibleCollaborators.map((person, index) => (
                  <Avatar
                    key={person.id || person.userId || index}
                    src={person.src || person.avatar || getUserAvatarUrl(person)}
                    name={person.name}
                    size="sm"
                    className="project-task-detail__collab-avatar"
                  />
                ))}
                {collaboratorTotal > 0 ? (
                  <span className="project-task-detail__collab-count">
                    {collaboratorTotal}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </header>

        <div className="project-task-detail__body">
          <section className="project-task-detail__section project-task-detail__section--description">
            <h3 className="project-task-detail__section-title project-task-detail__section-title--sm">
              Description
            </h3>
            {editingField === 'description' ? (
              <textarea
                ref={descriptionInputRef}
                className="project-task-detail__description-input"
                value={draftDescription}
                disabled={isSaving}
                rows={3}
                aria-label="Task description"
                onChange={(event) => setDraftDescription(event.target.value)}
                onBlur={() => finishEdit('description')}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    cancelEdit();
                  }
                }}
              />
            ) : (
              <button
                type="button"
                className="project-task-detail__description"
                onClick={() => beginEdit('description')}
                disabled={!canEdit}
              >
                {isTaskLoading && !draftDescription && !task.description
                  ? 'Loading description...'
                  : draftDescription ||
                    task.description ||
                    (canEdit ? 'Add a description...' : 'No Description Found')}
              </button>
            )}
          </section>

          <section className="project-task-detail__section project-task-detail__section--activity">
            <h3 className="project-task-detail__section-title">
              Session Activities
            </h3>
            <div className="project-task-detail__activity">
              {isActivityLoading ? (
                <p className="project-task-detail__activity-empty">
                  Loading session activity...
                </p>
              ) : null}
              {!isActivityLoading && activityGroups.length === 0 ? (
                <p className="project-task-detail__activity-empty">
                  No session activity yet.
                </p>
              ) : null}
              {!isActivityLoading &&
                activityGroups.map((group) => (
                  <section
                    key={group.dateKey}
                    className="project-task-detail__activity-day"
                    aria-label={`Session activities on ${group.dateLabel}`}
                  >
                    <div className="project-task-detail__activity-day-head">
                      <span className="project-task-detail__activity-day-date">
                        {group.dateLabel}
                      </span>
                    </div>
                    <div
                      className="project-task-detail__activity-day-divider"
                      aria-hidden="true"
                    />
                    <div className="project-task-detail__activity-day-list">
                      {group.cards.map((card, index) => {
                        const impression = impressions[String(card.id)] || {};
                        const likeCount = Number(impression.likeCount || 0);
                        const heartCount = Number(impression.heartCount || 0);
                        const cannotReact =
                          !canEdit ||
                          Boolean(reactingMemberId) ||
                          (currentUserId &&
                            String(card.userId) === String(currentUserId));

                        return (
                          <article
                            key={card.id || `${card.sessionId}-${index}`}
                            className="project-task-detail__activity-card"
                          >
                            <div className="project-task-detail__activity-head">
                              <div className="project-task-detail__activity-person">
                                <span className="project-task-detail__activity-avatar">
                                  <Avatar
                                    src={card.user.avatar}
                                    name={card.user.name}
                                    size="sm"
                                    className="project-task-detail__activity-avatar-img"
                                  />
                                  <span
                                    className="project-task-detail__activity-presence"
                                    aria-hidden="true"
                                  />
                                </span>
                                <div className="project-task-detail__activity-identity">
                                  <span className="project-task-detail__activity-user">
                                    {card.user.name}
                                  </span>
                                  <span
                                    className={`project-task-detail__activity-tag project-task-detail__activity-tag--${
                                      card.isLeft ? 'exited' : 'in-session'
                                    }`}
                                  >
                                    {card.status}
                                  </span>
                                </div>
                              </div>
                              <div className="project-task-detail__activity-meta">
                                <p className="project-task-detail__activity-logged">
                                  <span>Logged Hours:</span>
                                  <b>{card.loggedHours}</b>
                                </p>
                                <div className="project-task-detail__activity-impressions">
                                  <div className="project-task-detail__activity-impression-action">
                                    <CustomIconButton
                                      iconSrc={
                                        impression.liked
                                          ? ICONS.likeFill
                                          : ICONS.likeUnfill
                                      }
                                      label={
                                        impression.liked
                                          ? `Unlike ${card.user.name}`
                                          : `Like ${card.user.name}`
                                      }
                                      size="sm"
                                      aria-pressed={Boolean(impression.liked)}
                                      disabled={cannotReact || !card.sessionId}
                                      onClick={() =>
                                        handleToggleImpression(
                                          card.sessionId,
                                          card.id,
                                          'liked'
                                        )
                                      }
                                    />
                                    {likeCount > 0 ? (
                                      <span
                                        className="project-task-detail__activity-impression-count"
                                        aria-label={`${likeCount} likes`}
                                      >
                                        {likeCount}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="project-task-detail__activity-impression-action">
                                    <CustomIconButton
                                      iconSrc={
                                        impression.hearted
                                          ? ICONS.heartFill
                                          : ICONS.heartOutlineRed
                                      }
                                      label={
                                        impression.hearted
                                          ? `Remove heart from ${card.user.name}`
                                          : `Heart ${card.user.name}`
                                      }
                                      size="sm"
                                      aria-pressed={Boolean(impression.hearted)}
                                      disabled={cannotReact || !card.sessionId}
                                      onClick={() =>
                                        handleToggleImpression(
                                          card.sessionId,
                                          card.id,
                                          'hearted'
                                        )
                                      }
                                    />
                                    {heartCount > 0 ? (
                                      <span
                                        className="project-task-detail__activity-impression-count"
                                        aria-label={`${heartCount} hearts`}
                                      >
                                        {heartCount}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="project-task-detail__activity-notes">
                              <div className="project-task-detail__activity-note-row">
                                <p className="project-task-detail__activity-note">
                                  <span>Join Note:</span>
                                  <b>{card.joinNote}</b>
                                </p>
                                <span className="project-task-detail__activity-time">
                                  {card.joinTime}
                                </span>
                              </div>
                              <div className="project-task-detail__activity-note-row">
                                <p className="project-task-detail__activity-note">
                                  <span>Exit Note:</span>
                                  <b className={card.isLeft ? '' : 'is-muted'}>
                                    {card.exitNote}
                                  </b>
                                </p>
                                <span className="project-task-detail__activity-time">
                                  {card.exitTime}
                                </span>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ))}
            </div>
          </section>
        </div>

        <div className="project-task-detail__footer">
          <div className="project-task-detail__tracking-stats">
            <div>
              <b>{loggedHrs}</b>
              <span>Logged of {totalHrs}</span>
            </div>
            <div className="project-task-detail__tracking-stats-end">
              <b>{remainingHrs}</b>
              <span>Remaining</span>
            </div>
          </div>
          <ProgressBar
            value={loggedHours}
            max={estimatedHours}
            tone="green"
            className="project-task-detail__progress"
          />
          {canEdit &&
            (onStartSession || startSessionDisabled) ? (
            <StartSessionDropdown
              variant="primary"
              size="md"
              fullWidth
              playIconSrc={ICONS.playWhite}
              className="project-task-detail__start-btn"
              skipDropdown={sessionAction !== 'start'}
              menuPlacement="top"
              label={sessionLabel}
              disabled={startSessionDisabled}
              onSelect={onStartSession}
            />
          ) : null}
        </div>
      </aside>

      <DeleteConfirmation
        isOpen={deleteOpen}
        onCancel={() => (isDeleting ? null : setDeleteOpen(false))}
        onConfirm={handleDeleteConfirm}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        cancelText="Cancel"
        confirmText="Delete Task"
        isDeleting={isDeleting}
      />
    </>,
    document.body
  );
};

export default ProjectTaskDetail;
